import React from "react";
import { InputNumber } from "antd";
import {
  UploadOutlined,
  FileExcelOutlined,
  LoadingOutlined,
} from "@ant-design/icons";

export default function StepUploadProfile({
  uploadFile = null,
  setUploadFile,
  uploadError = false,
  setUploadError,
  solarDegradation = "0.4",
  setSolarDegradation,
  uploadDataType = "single",
  setUploadDataType,
  profileFilename = "",
  loadedFromHistory = false,
  uploadingProfile = false,
  handleDownloadTemplate,
  onBack,
  onSubmit,
}) {
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-4 max-w-xl">
        {profileFilename && !uploadFile && (
          <div className="p-3.5 bg-blue-50/80 border border-blue-200 rounded-xl flex items-center justify-between text-xs text-blue-900 shadow-sm">
            <div className="flex items-center gap-2.5">
              <FileExcelOutlined className="text-blue-600 text-base" />
              <div>
                <div>
                  <strong>Previously uploaded profile:</strong>{" "}
                  <code className="bg-white px-2 py-0.5 rounded border border-blue-200 text-blue-700 font-semibold">
                    {profileFilename}
                  </code>
                </div>
                <div className="text-[11px] text-blue-600/80 mt-0.5">
                  Proceeding without uploading a new file will use this existing data.
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-3 text-slate-700">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
            Data Upload Type
          </label>
          <div className="flex p-1 bg-slate-100 rounded-xl w-fit mb-2 border border-slate-200">
            <button
              type="button"
              onClick={() => {
                setUploadDataType("single");
                if (!solarDegradation) setSolarDegradation("0.4");
              }}
              className={`px-5 py-2 text-sm font-semibold rounded-lg transition-all ${
                uploadDataType === "single"
                  ? "bg-white text-[#0052cc] shadow-sm border border-slate-200/60"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 border border-transparent"
              }`}
            >
              Single-Year Data
            </button>
            <button
              type="button"
              onClick={() => {
                setUploadDataType("full");
                setSolarDegradation("");
              }}
              className={`px-5 py-2 text-sm font-semibold rounded-lg transition-all ${
                uploadDataType === "full"
                  ? "bg-white text-[#0052cc] shadow-sm border border-slate-200/60"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 border border-transparent"
              }`}
            >
              Full Project Tenure Data
            </button>
          </div>
        </div>

        {uploadDataType === "single" && (
          <div className="flex flex-col gap-1 text-slate-700 mt-2">
            <label>
              Solar Degradation Rate (% per Year) <span className="text-red-500">*</span>
            </label>
            <InputNumber
              min={0}
              max={100}
              step={0.01}
              controls={false}
              value={solarDegradation}
              onChange={(val) => setSolarDegradation(val?.toString() || "")}
              className="w-full h-10 rounded-xl text-sm border-slate-200"
              placeholder="e.g. 0.5"
            />
            <span className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
              This rate will be used to automatically simulate subsequent years based on the single year uploaded.
            </span>
          </div>
        )}

        <div className="flex flex-col gap-1 text-slate-700 pt-2">
          <label>Select Generation Excel File (.xlsx)</label>
          <div
            className={`border-2 border-dashed rounded-2xl p-8 text-center bg-slate-50/50 hover:border-slate-300 transition-colors flex flex-col items-center justify-center ${
              uploadError ? "border-red-500 bg-red-50/10" : "border-slate-200"
            }`}
          >
            <UploadOutlined className="text-slate-400 text-3xl mb-3" />
            <p className="text-xs text-slate-500 mb-4 leading-relaxed max-w-sm">
              Drag and drop your generation profile Excel spreadsheet here, or select browse files. Make sure the
              template format matches the selected data type above.
            </p>
            <div className="flex gap-3 justify-center mb-1">
              <input
                type="file"
                id="hybrid-step-file-upload"
                className="hidden"
                accept=".xlsx"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setUploadFile(file);
                  if (file) {
                    setUploadError(false);
                  }
                }}
              />
              <button
                type="button"
                onClick={() => document.getElementById("hybrid-step-file-upload").click()}
                className="h-9 px-4 bg-white border border-slate-200 text-slate-700 hover:text-slate-400 rounded-lg text-xs font-semibold shadow-sm transition-all cursor-pointer whitespace-nowrap"
              >
                Browse Files
              </button>
              <button
                type="button"
                onClick={handleDownloadTemplate}
                className="h-9 px-4 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-100 rounded-lg text-xs font-semibold shadow-sm transition-all cursor-pointer flex items-center gap-1 whitespace-nowrap"
              >
                <FileExcelOutlined /> Download Template
              </button>
            </div>
            {uploadFile && (
              <div className="mt-4 text-xs text-emerald-600 font-semibold animate-fade-in">
                Selected: <span className="text-[#0052CC]">{uploadFile.name}</span> (
                {(uploadFile.size / 1024).toFixed(1)} KB)
              </div>
            )}
            {uploadError && (
              <div className="text-red-500 text-xs mt-2 font-semibold">
                Please upload a valid generation profile spreadsheet before continuing.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center pt-6 border-t border-slate-100 mt-8">
        <button type="button" onClick={onBack} className="bess-btn-secondary">
          Back
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={uploadingProfile}
          className="bess-btn-primary"
        >
          {uploadingProfile ? (
            <LoadingOutlined />
          ) : (loadedFromHistory || profileFilename) && !uploadFile ? (
            "Use Existing Profile & Continue →"
          ) : (
            "Upload & Continue →"
          )}
        </button>
      </div>
    </div>
  );
}
